<script lang="ts">
  /**
   * Pagination з кнопками сторінок та селектором розміру сторінки.
   *
   *   ← 1 2 3 ... 7 →                            10 [20] 50 100
   *
   * Сторінки 1-indexed. Якщо total == 0 — компонент не рендерить нічого.
   */
  let {
    total,
    page,
    pageSize,
    onpage,
    onpagesize,
  }: {
    total: number;
    page: number;
    pageSize: number;
    onpage: (p: number) => void;
    onpagesize: (s: number) => void;
  } = $props();

  const PAGE_SIZES = [10, 20, 50, 100];

  const totalPages = $derived(Math.max(1, Math.ceil(total / pageSize)));

  /**
   * Перетворює (current, total) у послідовність кнопок: цілі числа -- це
   * сторінки які треба рендерити, рядок '...' -- розрив із крапками.
   * Логіка показує перший, останній, current ± 1.
   */
  function buildRange(current: number, last: number): (number | '...')[] {
    if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
    const sib = 1;
    const leftBoundary = Math.max(2, current - sib);
    const rightBoundary = Math.min(last - 1, current + sib);
    const showLeftDots = leftBoundary > 2;
    const showRightDots = rightBoundary < last - 1;

    if (!showLeftDots && showRightDots) {
      // 1 2 3 4 5 ... last
      const left = Array.from({ length: 3 + 2 * sib }, (_, i) => i + 1);
      return [...left, '...', last];
    }
    if (showLeftDots && !showRightDots) {
      // 1 ... (last-4) (last-3) (last-2) (last-1) last
      const len = 3 + 2 * sib;
      const right = Array.from({ length: len }, (_, i) => last - len + i + 1);
      return [1, '...', ...right];
    }
    // 1 ... current-1 current current+1 ... last
    const middle = Array.from({ length: 2 * sib + 1 }, (_, i) => leftBoundary + i);
    return [1, '...', ...middle, '...', last];
  }

  const pages = $derived(buildRange(page, totalPages));
</script>

{#if total > 0}
  <div class="flex items-center justify-between gap-3 flex-wrap">
    <div class="flex items-center gap-1">
      <button
        type="button"
        onclick={() => onpage(page - 1)}
        disabled={page <= 1}
        class="grid h-8 w-8 place-items-center rounded-md border border-[var(--color-border-3)] bg-white text-xs font-medium hover:bg-[var(--color-bg-3)] disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Попередня"
      >←</button>
      {#each pages as p, i (i)}
        {#if p === '...'}
          <span class="px-1 text-[var(--color-text-muted-2)] text-xs">…</span>
        {:else}
          <button
            type="button"
            onclick={() => onpage(p)}
            class="grid h-8 min-w-[32px] px-2 place-items-center rounded-md border text-xs font-medium"
            class:border-[var(--color-pu)]={p === page}
            class:bg-[var(--color-pu)]={p === page}
            class:text-white={p === page}
            class:border-[var(--color-border-3)]={p !== page}
            class:bg-white={p !== page}
            class:hover:bg-[var(--color-bg-3)]={p !== page}
          >{p}</button>
        {/if}
      {/each}
      <button
        type="button"
        onclick={() => onpage(page + 1)}
        disabled={page >= totalPages}
        class="grid h-8 w-8 place-items-center rounded-md border border-[var(--color-border-3)] bg-white text-xs font-medium hover:bg-[var(--color-bg-3)] disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Наступна"
      >→</button>
    </div>

    <div class="flex items-center gap-1 rounded-md border border-[var(--color-border-3)] bg-white p-0.5">
      {#each PAGE_SIZES as s}
        <button
          type="button"
          onclick={() => onpagesize(s)}
          class="h-7 min-w-[32px] px-2 rounded text-xs font-semibold"
          class:bg-[var(--color-pu)]={s === pageSize}
          class:text-white={s === pageSize}
          class:text-[var(--color-text-muted)]={s !== pageSize}
          class:hover:text-[var(--color-text)]={s !== pageSize}
        >{s}</button>
      {/each}
    </div>
  </div>
{/if}
